import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  CircularProgress,
  Button,
  Container
} from '@mui/material';
import {
  TrendingUp as OpportunitiesIcon,
  Business as CompaniesIcon,
  People as ContactsIcon,
  AccessTime as TimeIcon,
  Support as SupportIcon,
  Person as TalentIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { contactService } from '../services/contactService';
import { companyService } from '../services/companyService';
import { opportunityService } from '../services/opportunityService';
import { supportContractService } from '../services/supportContractService';
import { timeEntryService } from '../services/timeEntryService';
import { talentService } from '../services/talentService';
import WonDealsByLOBChart from '../components/WonDealsByLOBChart';
import OpportunitiesByMonthChart from '../components/OpportunitiesByMonthChart';
import TopCustomersSection from '../components/TopCustomersSection';
import LoadingSpinner from '../components/LoadingSpinner';
import { DEAL_STAGES } from '../constants/dealStages';
import getApiConfig from '../config/apiConfig';
import { logError } from '../utils/errorLogger';

const DashboardTile = ({ title, icon, count, path, countLabel = "Count", showCount = true }) => {
  const navigate = useNavigate();
  
  return (
    <Box sx={{ 
      width: '100%',
      height: '120px'
    }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          cursor: 'pointer',
          transition: 'transform 0.2s',
          height: '100%',
          width: '100%',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
        }}
        onClick={() => navigate(path)}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 1
        }}>
          {React.cloneElement(icon, { sx: { ...icon.props.sx, fontSize: 32 } })}
          <Typography 
            variant="h6" 
            sx={{ 
              ml: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '1.1rem'
            }}
          >
            {title}
          </Typography>
        </Box>
        {showCount && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 12,
              fontSize: '0.9rem',
              fontWeight: 'medium'
            }}
          >
            {countLabel}: {count}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

const Dashboard = () => {
  const [counts, setCounts] = useState({
    opportunities: 0,
    companies: 0,
    contacts: 0,
    talents: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCounts();
  }, []);

  const loadCounts = async () => {
    setLoading(true);
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/admin/counts`);
      if (!response.ok) throw new Error('Failed to fetch dashboard counts');
      const data = await response.json();
      setCounts({
        opportunities: data.ActiveOpportunitiesCount || 0,
        companies: data.CompaniesCount || 0,
        contacts: data.ContactsCount || 0,
        talents: data.ActiveTalentsCount || 0,
      });
    } catch (error) {
      logError('Dashboard', 'loadCounts', error);
      setCounts({
        opportunities: 0,
        companies: 0,
        contacts: 0,
        talents: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const crmTiles = [
    {
      title: 'Opportunities',
      icon: <OpportunitiesIcon sx={{ fontSize: 40, color: '#2196f3' }} />, 
      count: counts.opportunities,
      path: '/opportunities',
      countLabel: 'Active Opportunities'
    },
    {
      title: 'Companies',
      icon: <CompaniesIcon sx={{ fontSize: 40, color: '#4caf50' }} />, 
      count: counts.companies,
      path: '/companies'
    },
    {
      title: 'Contacts',
      icon: <ContactsIcon sx={{ fontSize: 40, color: '#ff9800' }} />, 
      count: counts.contacts,
      path: '/contacts'
    },
  ];

  const operationTiles = [
    {
      title: 'Time Tracker',
      icon: <TimeIcon sx={{ fontSize: 40, color: '#9c27b0' }} />,
      path: '/time-tracker',
      showCount: false
    },
    {
      title: 'Support Ticket',
      icon: <SupportIcon sx={{ fontSize: 40, color: '#f44336' }} />,
      path: '/support-contracts',
      showCount: false
    },
    {
      title: 'Talent Pool',
      icon: <TalentIcon sx={{ fontSize: 40, color: '#795548' }} />,
      count: counts.talents,
      path: '/talents',
      countLabel: 'Active Talents'
    },
  ];

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, ml: 0 }}>
        <DashboardIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4">
          Dashboard
        </Typography>
      </Box>
      
      {loading ? (
        <LoadingSpinner 
          loading={true} 
          message="Loading dashboard data..." 
          size={60}
        />
      ) : (
        <>
          {/* CRM Section */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                display: 'inline-block'
              }}
            >
              CRM
            </Typography>
            <Box 
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 1.5,
                width: '100%',
                pl: 0
              }}
            >
              {crmTiles.map((tile) => (
                <Box key={tile.title}>
                  <DashboardTile {...tile} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Operation Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                color: 'secondary.main',
                borderBottom: '2px solid',
                borderColor: 'secondary.main',
                pb: 1,
                display: 'inline-block'
              }}
            >
              Operation
            </Typography>
            <Box 
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 1.5,
                width: '100%',
                pl: 0
              }}
            >
              {operationTiles.map((tile) => (
                <Box key={tile.title}>
                  <DashboardTile {...tile} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Charts Section */}
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)'
              },
              gap: 3,
              width: '100%',
              pl: 0,
              mb: 3
            }}
          >
            <WonDealsByLOBChart />
            <OpportunitiesByMonthChart />
          </Box>

          {/* Top Customers Section */}
          <TopCustomersSection />
        </>
      )}
    </Box>
  );
};

export default Dashboard; 